import { Button, ComboboxItem, Flex, MultiSelect } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpandMoreRounded } from '~/components/icons';
import { countryList } from '~/constants';
import { memberStatusOptions } from '~/routes/_common.manage-members/fragments/options';
import classes from '~/routes/_fullpage.manage-resources.add.resource-detail-update.$id/ResourceDetails.module.css';
import { useSpecialitiesListAll } from '~/services/specialities';
import { useUserExport } from '~/services/user';

interface ExportMembersProps {
  onClose: () => void;
  exportMembers: () => void;
  isExporting: boolean;
}

interface ExportMemberRequest {
  marketing_email: string[];
  location: string[];
  specialities_ids: number[];
  status: string[];
}

const marketingData = [
  { key: '', value: 'All' },
  { key: 'accept', value: 'Accept Marketing' },
  {
    key: 'decline',
    value: 'Decline Marketing',
  },
];

export const ExportMembers = ({
  onClose,
  exportMembers,
  isExporting,
}: ExportMembersProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm<ExportMemberRequest>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { isPending: isLoading, mutateAsync } = useUserExport();

  const [specialities, setSpecialities] = useState<ComboboxItem[]>([]);
  const { data: specialitiesOption } = useSpecialitiesListAll({});

  // Helper function to handle "All" selection logic
  const handleAllSelection = (
    newValue: string[],
    fieldName: string,
    allValue: string = ''
  ) => {
    const hasAll = newValue.includes(allValue);
    const otherValues = newValue.filter((val) => val !== allValue);

    if (hasAll && otherValues.length > 0) {
      // If "All" is selected along with other values, keep only "All"
      setValue(fieldName as keyof ExportMemberRequest, [allValue] as any);
      return [allValue];
    } else if (!hasAll && otherValues.length > 0) {
      // If specific values are selected, ensure "All" is not included
      setValue(fieldName as keyof ExportMemberRequest, otherValues as any);
      return otherValues;
    } else {
      // Normal behavior for other cases
      setValue(fieldName as keyof ExportMemberRequest, newValue as any);
      return newValue;
    }
  };

  useEffect(() => {
    if (specialitiesOption?.data?.data?.length && !specialities.length) {
      const sTemp: ComboboxItem[] = [{ label: 'All', value: '' }];
      specialitiesOption.data.data.map((s) => {
        const d: ComboboxItem = {
          label: s.name,
          value: s.name.toString(),
        };
        sTemp.push(d);
        return d;
      });
      setSpecialities(sTemp);
    }
  }, [specialities.length, specialitiesOption]);

  const onSubmit = async (data: ExportMemberRequest) => {
    try {
      // When location = 'All', omit it from the request (treat as empty)
      const requestData = {
        ...data,
        location: data.location?.includes('All') ? [] : data.location,
      };

      // Mock export functionality for testing
      console.log('Export Members - Form Data:', {
        marketing_email: requestData.marketing_email,
        location: requestData.location,
        specialities_ids: requestData.specialities_ids,
        status: requestData.status,
      });

      await mutateAsync(requestData);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <Flex direction="column" w="100%" gap="md">
      <Flex direction="column" gap="md">
        <Controller
          name="marketing_email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MultiSelect
              data={marketingData}
              label="Markting Email"
              placeholder="Select Marketing Email"
              searchable
              error={error?.message}
              rightSection={<ExpandMoreRounded fill="var(--action-active)" />}
              rightSectionPointerEvents="none"
              {...field}
              onChange={(value) => {
                const updatedValue = handleAllSelection(
                  value,
                  'marketing_email',
                  ''
                );
                field.onChange(updatedValue);
              }}
            />
          )}
        />

        <Controller
          name="location"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MultiSelect
              data={['All', ...countryList]}
              label="Location"
              placeholder="Select Location"
              searchable
              error={error?.message}
              rightSection={<ExpandMoreRounded fill="var(--action-active)" />}
              rightSectionPointerEvents="none"
              {...field}
              onChange={(value) => {
                const updatedValue = handleAllSelection(
                  value,
                  'location',
                  'All'
                );
                field.onChange(updatedValue);
              }}
            />
          )}
        />

        <Controller
          name="specialities_ids"
          control={control}
          render={({ field, fieldState: { error } }) => (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            <MultiSelect
              size="md"
              data={specialities}
              error={error?.message}
              label={'Specialities'}
              placeholder="Select Specialities"
              withAsterisk
              classNames={{
                inputField: classes.MultiSelect,
              }}
              {...field}
              onChange={(value) => {
                const updatedValue = handleAllSelection(
                  value,
                  'specialities_ids',
                  ''
                );
                field.onChange(updatedValue);
              }}
            />
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MultiSelect
              data={[{ label: 'All', value: '' }, ...memberStatusOptions(t)]}
              label="Status"
              placeholder="Select Status"
              searchable
              error={error?.message}
              rightSection={<ExpandMoreRounded fill="var(--action-active)" />}
              rightSectionPointerEvents="none"
              {...field}
              onChange={(value) => {
                const updatedValue = handleAllSelection(value, 'status', '');
                field.onChange(updatedValue);
              }}
            />
          )}
        />
      </Flex>
      <Flex direction="row" justify="end" gap="md">
        <Button
          variant="outline"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="filled"
          onClick={handleSubmit(onSubmit)}
          disabled={isExporting || isLoading}
        >
          {isLoading ? 'Exporting...' : 'Export List'}
        </Button>
      </Flex>
    </Flex>
  );
};
